import { Component, Vue } from 'vue-property-decorator';
import { setFeeds } from '@/store/FeedsStore/models';
import { setWeights } from '@/store/WeightsStore/models';
import ChartCard from '@/components/ChartCard.component/ChartCard.component';
import { State } from '@/models/interfaces/State.interface';
import { Feed, Weight } from '@/models/interfaces';

@Component({
  components: {
    ChartCard
  }
})
export default class Dashboard extends Vue {
  public datemenu = false;
  public date = '';

  mounted(): void {
    this.$store.dispatch(setFeeds);
    this.$store.dispatch(setWeights);
  }
  //#region Dates
  get todaysDate() {
    return this.formattedDate(new Date());
  }

  get feeds() {
    return (this.$store.state as State).FeedsStore.feeds;
  }

  get lastWeekDate() {
    var pastDate = new Date().getDate() - 7;
    return this.formattedDate(new Date(new Date().setDate(pastDate)));
  }

  get lastMonthDate() {
    var pastDate = new Date().getDate() - 30;
    return this.formattedDate(new Date(new Date().setDate(pastDate)));
  }
  //#endregion

  //#region Specific Date Feed Chart
  get specificDateFeedIntakeAmount() {
    return (this.$store.getters.getDailyFeedIntakeAmount(this.date) as Feed[]).map((feed) => parseInt(feed.feedAmount, 10));
  }

  get specificDateFeedIntakeTimes() {
    return (this.$store.getters.getDailyFeedIntakeAmount(this.date) as Feed[]).map((feed) => feed.time);
  }

  get specificDateFeedIntakeChart() {
    return {
      data: {
        labels: [...this.specificDateFeedIntakeTimes],
        series: [
          this.specificDateFeedIntakeAmount
        ]
      },
      options: {
        low: 0,
        high: Math.max(...this.specificDateFeedIntakeAmount) * 1.1, // creative tim: we recommend you to set the high sa the biggest value + something for a better look
        chartPadding: {
          top: 20,
          right: 10,
          bottom: 15,
          left: 10
        },
        ticks: [0, 25, 50],
      }
    };
  }
  //#endregion

  //#region Average Feeding Time
  get PieChartData() {
    const times = ['00:00', '03:00', '06:00', '09:00', '12:00', '15:00', '18:00', '21:00']
    const values: number[] = []
    for (var i = 0; i < times.length; i++) {
      if (i === times.length - 1) {
        values.push(this.getAllFeedsInTimeRange(times[i], '23:59').length);
      } else {
        values.push(this.getAllFeedsInTimeRange(times[i], times[i + 1]).length);
      }
    }
    return values;
  }

  private PieChartLabels = ['12-3 AM', '3-6 AM', '6-9 AM', '9-12 AM/PM', '12-3 PM', '3-6 PM', '6-9 PM', '9-12 PM/AM'];

  get PieChart() {
    return {
      data: {
        labels: this.PieChartLabels,
        series: [
          this.PieChartData
        ]
      },
      options: {
        seriesBarDistance: this.TotalPointsPie,
        chartPadding: {
          top: 30,
          right: 10,
          bottom: 35,
          left: 10
        },
      }
    };
  }

  get TotalPointsPie() {
    return this.PieChartData.reduce((a, b) => a + b, 0);
  }
  //#endregion

  //#region Daily Feed Charts
  get dailyFeedIntakeAmount() {
    return (this.$store.getters.getDailyFeedIntakeAmount(this.todaysDate) as Feed[]).map((feed) => parseInt(feed.feedAmount, 10));
  }

  get dailyFeedIntakeTimes() {
    return (this.$store.getters.getDailyFeedIntakeAmount(this.todaysDate) as Feed[]).map((feed) => feed.time);
  }

  get dailyFeedIntakeChart() {
    return {
      data: {
        labels: [...this.dailyFeedIntakeTimes],
        series: [
          this.dailyFeedIntakeAmount
        ]
      },
      options: {
        low: 0,
        high: Math.max(...this.dailyFeedIntakeAmount) * 1.1, // creative tim: we recommend you to set the high sa the biggest value + something for a better look
        chartPadding: {
          top: 20,
          right: 10,
          bottom: 15,
          left: 10
        },
        ticks: [0, 25, 50],
      }
    };
  }
  //#endregion

  //#region Weekly Feed Charts
  get weeklyFeedIntakeAmount(): number[] {
    const amount = [];
    for (var i = 7; i >= 0; i--) {
      var pastDate = new Date(new Date().setDate(new Date().getDate() - i));
      amount.push((this.$store.getters.getDailyFeedIntakeAmount((this.formattedDate(pastDate))) as Feed[]).filter(feed => !!feed).reduce((a, b) => a + parseInt(b.feedAmount, 10), 0));
    }
    return amount;
  }

  get weeklyFeedIntakeTimes(): string[] {
    const amount = [];
    for (var i = 7; i >= 0; i--) {
      var pastDate = new Date(new Date().setDate(new Date().getDate() - i));
      amount.push(this.formattedDate(pastDate, false));
    }
    return amount;
  }

  get weeklyFeedIntakeChart() {
    return {
      data: {
        labels: [...this.weeklyFeedIntakeTimes],
        series: [
          this.weeklyFeedIntakeAmount
        ]
      },
      options: {
        low: 0,
        high: Math.max(...this.weeklyFeedIntakeAmount) * 1.1, // creative tim: we recommend you to set the high sa the biggest value + something for a better look
        chartPadding: {
          top: 20,
          right: 10,
          bottom: 15,
          left: 10
        },
        ticks: [0, 25, 50],
      }
    };
  }
  //#endregion

  //#region Monthly Weights 
  get weightsInMonth() {
    return (this.$store.state as State).WeightsStore.weights
      .sort((a, b) => {
        const aDate = new Date(a.date);
        const bDate = new Date(b.date);

        return aDate > bDate ? 1 : aDate < bDate ? -1 : 0;
      }).map((weight) => parseInt(weight.amount, 0));
  }

  get weightsMonthDates(): string[] {
    return (this.$store.state as State).WeightsStore.weights
      .sort((a, b) => {
        const aDate = new Date(a.date);
        const bDate = new Date(b.date);

        return aDate > bDate ? 1 : aDate < bDate ? -1 : 0;
      }).map((weight) => this.formattedDate(new Date(weight.date), false));
  }

  get monthlyWeightsChart() {
    return {
      data: {
        labels: [...this.weightsMonthDates],
        series: [
          this.weightsInMonth
        ]
      },
      options: {
        low: Math.min(...this.weightsInMonth) * 0.75,
        high: Math.max(...this.weightsInMonth) * 1.1, // creative tim: we recommend you to set the high sa the biggest value + something for a better look
        chartPadding: {
          top: 20,
          right: 15,
          bottom: 15,
          left: 15
        },
        ticks: [0, 25, 50],
      }
    };
  }
  //#endregion

  get charts() {
    return [{
      name: 'Today\'s Bottle Feed Intakes',
      chart: this.dailyFeedIntakeChart,
      color: 'info'
    },
    {
      name: 'Weekly Bottle Feed Intakes',
      chart: this.weeklyFeedIntakeChart,
      color: 'green'
    },
    {
      name: 'Monthly Weights',
      chart: this.monthlyWeightsChart,
      color: 'red'
    }];
  }

  //#region Helpers
  private formattedDate(date: Date, addYear: boolean = true): string {
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0'); //January is 0!
    const yyyy = date.getFullYear();
    return `${addYear ? yyyy + '-' : ''}${mm}-${dd}`;
  }

  private getAllFeedsInTimeRange(start: string, end: string): Feed[] {
    return this.feeds.filter((feed) => {
      const dateString = '1970/01/01 ';
      const feedTime = new Date(dateString + feed.time);
      const startDate = new Date(dateString + start)
      const endDate = new Date(dateString + end);
      return startDate <= feedTime && endDate > feedTime;
    }) || [];
  }
  //#endregion
}
